import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { WORKS } from "@/mocks/works";
import RankingItem from "@/components/base/RankingItem";
import { usePageMeta } from "@/hooks/usePageMeta";

type TabKey = "week" | "month" | "all" | "rating";

const TABS: { key: TabKey; label: string; hint: string }[] = [
  { key: "week", label: "周榜", hint: "近 7 天讨论与追更热度" },
  { key: "month", label: "月榜", hint: "近 30 天综合热度" },
  { key: "all", label: "总榜", hint: "累计人气总排行" },
  { key: "rating", label: "高分榜", hint: "按读者综合评分排序" },
];

export default function Ranking() {
  usePageMeta({
    title: "人气韩漫排行榜｜韩漫人气榜、热度排名、高分榜 TOP 榜单",
    description:
      "人气韩漫大全排行榜实时更新，提供韩漫人气榜、月榜、总榜与高分榜，热度排名与高分作品一目了然，帮你快速锁定当前最值得追的韩漫。",
    path: "/ranking",
  });

  const [tab, setTab] = useState<TabKey>("week");

  const list = useMemo(() => {
    const base = [...WORKS];
    switch (tab) {
      case "week":
        return base.sort((a, b) => b.popularity - a.popularity);
      case "month":
        return base.sort((a, b) => b.popularity * 0.9 + b.rating * 10 - (a.popularity * 0.9 + a.rating * 10));
      case "all":
        return base.sort((a, b) => b.popularity * 1.05 + b.episodes.length - (a.popularity * 1.05 + a.episodes.length));
      case "rating":
      default:
        return base.sort((a, b) => b.rating - a.rating);
    }
  }, [tab]);

  const active = TABS.find((t) => t.key === tab)!;

  return (
    <div className="mx-auto max-w-7xl px-4 py-24 md:px-6 md:py-28">
      <nav className="mb-6 text-xs text-foreground-500" aria-label="面包屑">
        <ol className="flex flex-wrap items-center gap-1.5">
          <li><Link to="/" className="hover:text-primary-600">首页</Link></li>
          <li><i className="ri-arrow-right-s-line"></i></li>
          <li aria-current="page" className="text-foreground-700">人气排行榜</li>
        </ol>
      </nav>

      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="font-label text-xs font-semibold uppercase tracking-widest text-primary-600">Ranking</p>
          <h1 className="mt-1 font-heading text-3xl font-bold text-foreground-950 md:text-4xl">人气韩漫排行榜</h1>
          <p className="mt-3 max-w-2xl text-sm leading-relaxed text-foreground-600">
            想看韩漫又怕踩雷？周榜、月榜、总榜与高分榜实时更新，热度排名与高分作品一目了然，帮你快速锁定当前最值得追的韩漫。
          </p>
        </div>
      </div>

      <div className="mt-8 flex flex-wrap items-center justify-between gap-3 border-b border-background-200">
        <div className="flex gap-1 rounded-full border border-background-200 bg-background-100 p-1">
          {TABS.map((t) => (
            <button
              key={t.key}
              type="button"
              onClick={() => setTab(t.key)}
              className={`whitespace-nowrap rounded-full px-4 py-2 text-sm font-medium transition-colors md:px-5 ${
                tab === t.key ? "bg-primary-500 text-background-50" : "text-foreground-600 hover:bg-background-50"
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>
        <p className="text-xs text-foreground-500">
          <i className="ri-information-line mr-1 text-primary-500"></i>
          {active.hint}
        </p>
      </div>

      <ol className="mt-6 grid grid-cols-1 gap-3 xl:grid-cols-2">
        {list.map((work, i) => (
          <RankingItem key={work.id} work={work} rank={i + 1} />
        ))}
      </ol>

      <p className="mt-8 text-center text-xs text-foreground-500">
        榜单数据综合读者人气、评分与讨论热度，实时动态更新。评分与排名仅供参考，以各正版平台实际连载为准。
      </p>
    </div>
  );
}